import { Box, CircularProgress, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { log } from '@/logger/logger.ts';
import { Input } from '@/UI/Input/Input';
import { I18N_KEYS } from '@/language/keys';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { useImportModelView } from '@/components/Recipe/FlowContext';
import { BLOCKED_MODELS } from '@/consts/model-import.consts';
import { hasEditingPermissions } from '../Utils';
import type { ModelBaseNodeData } from '@/types/nodes/model';

const logger = log.getLogger('ImportModelInput');

interface ImportModelInputProps {
  data: ModelBaseNodeData;
  id: string;
}

export const ImportModelInput = ({ data, id }: ImportModelInputProps) => {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const { model, pastedData } = data;
  const [modelNotSupported, setModelNotSupported] = useState<string | null>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [cannotFindModel, setCannotFindModel] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);

  const importModelView = useImportModelView(id);

  /**
   * Import a model from its identifier - can be URL or name
   */
  const importModelFromUrl = useCallback(
    async (modelIdentifier: string) => {
      setModelNotSupported(null);
      setModelLoading(true);
      setCannotFindModel(false);

      for (const blockedModel of BLOCKED_MODELS) {
        if (modelIdentifier.includes(blockedModel)) {
          setModelNotSupported(blockedModel);
          setModelLoading(false);
          return;
        }
      }

      try {
        const success = await importModelView.importModel(modelIdentifier);
        if (!success) {
          setCannotFindModel(true);
        }
      } catch (e) {
        logger.error('Could not get model version change', e);
        setCannotFindModel(true);
      }

      setModelLoading(false);
    },
    [setModelNotSupported, importModelView],
  );

  // Auto-import model if identifier is provided
  useEffect(() => {
    if (
      pastedData?.importedModelIdentifier &&
      typeof pastedData?.importedModelIdentifier === 'string' &&
      !model?.version
    ) {
      void importModelFromUrl(pastedData?.importedModelIdentifier);
    }
  }, [importModelFromUrl, model?.version, pastedData?.importedModelIdentifier]);

  const handleModelNamePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      void importModelFromUrl(event.clipboardData.getData('text'));
    },
    [importModelFromUrl],
  );

  return (
    <Box className={isFocused ? 'nowheel nodrag nopan' : ''} sx={{ width: '100%', position: 'relative' }}>
      <Input
        autoComplete="off" // prevent autocomplete
        className="nodrag"
        disabled={!hasEditingPermissions(role, data)}
        helperText={
          modelNotSupported
            ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.MODEL_NOT_SUPPORTED, {
                modelType: modelNotSupported,
              })
            : cannotFindModel
              ? t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.INPUT_ERROR)
              : undefined
        }
        fullWidth
        error={cannotFindModel || !!modelNotSupported}
        onBlur={() => {
          setIsFocused(false);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => void handleModelNamePaste(event)}
        placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.WILDCARD.PLACEHOLDER)}
        size="large"
        value={pastedData?.importedModelIdentifier || model?.name || ''}
        endAdornment={
          <InputAdornment position="end">
            {modelLoading && <CircularProgress size={14} color="weavy_cta_secondary" />}
          </InputAdornment>
        }
      />
    </Box>
  );
};
